import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import U2000ActionBtn from "../../../../../u2000Core/components/buttons/U2000ActionBtn";
import { NS } from "../../../../U3121";
import type { U3121BillingDocumentNature, U3121BillingSummary, U3121BillingTecPool } from "../../../../U3121Service";
import { useU3121GetBillingSummaryHandlers } from "../../../../U3121UseBillingSummaryHandlers";
import U3121BillingSummaryDocumentsSection from "./U3121BillingSummaryDocumentsSection";
import U3121BillingSummaryReadinessBanner from "./U3121BillingSummaryReadinessBanner";
import U3121BillingSummaryProfitabilitySection from "./U3121BillingSummaryProfitabilitySection";
import U3121BillingSummaryTecSection from "./U3121BillingSummaryTecSection";
import U3121BillingSummaryTimesheetsSection from "./U3121BillingSummaryTimesheetsSection";
import U3121BillingSummaryInvoiceDetailDlg from "./detail/U3121BillingSummaryInvoiceDetailDlg";
import U3121BillingSummaryTecDetailDlg from "./detail/U3121BillingSummaryTecDetailDlg";
import U3121BillingSummaryTimesheetDetailDlg from "./detail/U3121BillingSummaryTimesheetDetailDlg";

export interface U3121ProjectBillingSummaryBodyProps {
  projectId: string;
  projectStatus: boolean;
}

type TecDetailState = { open: true; pool: U3121BillingTecPool } | { open: false };
type InvoiceDetailState = { open: true; documentNature: U3121BillingDocumentNature } | { open: false };

const U3121ProjectBillingSummaryBody: React.FC<U3121ProjectBillingSummaryBodyProps> = ({ projectId, projectStatus }) => {
  const { t } = useTranslation();
  const summaryHandlers = useU3121GetBillingSummaryHandlers();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState<U3121BillingSummary | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [tecDetail, setTecDetail] = useState<TecDetailState>({ open: false });
  const [invoiceDetail, setInvoiceDetail] = useState<InvoiceDetailState>({ open: false });
  const [timesheetDetailOpen, setTimesheetDetailOpen] = useState(false);

  const loadSummary = useCallback(async () => {
    const pid = projectId?.trim();
    if (!pid) {
      setSummary(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadFailed(false);
    const result = await summaryHandlers.fetch(pid);
    setSummary(result);
    setLoadFailed(result == null);
    setLoading(false);
  }, [projectId, summaryHandlers]);

  useEffect(() => {
    void loadSummary();
  }, [loadSummary]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-gray-600">{t("msgLoading", { ns: NS })}</p>
      </div>
    );
  }

  if (loadFailed || !summary) {
    return (
      <div className="flex flex-col items-center gap-4 py-12">
        <p className="text-gray-600">{t("msgBillingSummaryLoadError", { ns: NS })}</p>
        <U2000ActionBtn label={t("btnRefreshSummary", { ns: NS })} handleClick={() => void loadSummary()} />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex justify-end">
        <U2000ActionBtn label={t("btnRefreshSummary", { ns: NS })} handleClick={() => void loadSummary()} variant="secondary" />
      </div>

      <U3121BillingSummaryReadinessBanner summary={summary} projectStatus={projectStatus} t={t} />
      {summary.projectProfitability != null ? (
        <U3121BillingSummaryProfitabilitySection profitability={summary.projectProfitability} t={t} />
      ) : null}
      <div className="grid grid-cols-1 items-stretch gap-8 lg:grid-cols-4">
        <div className="flex min-h-0 flex-col lg:col-span-1">
          <U3121BillingSummaryTimesheetsSection summary={summary} onOpenDetail={() => setTimesheetDetailOpen(true)} t={t} />
        </div>
        <div className="flex min-h-0 flex-col lg:col-span-3">
          <U3121BillingSummaryTecSection summary={summary} onOpenDetail={(pool) => setTecDetail({ open: true, pool })} t={t} />
        </div>
      </div>
      <U3121BillingSummaryDocumentsSection
        summary={summary}
        onOpenDetail={(documentNature) => setInvoiceDetail({ open: true, documentNature })}
        t={t}
      />

      {tecDetail.open ? (
        <U3121BillingSummaryTecDetailDlg
          isOpen
          onClose={() => setTecDetail({ open: false })}
          projectId={projectId}
          pool={tecDetail.pool}
        />
      ) : null}

      {invoiceDetail.open ? (
        <U3121BillingSummaryInvoiceDetailDlg
          isOpen
          onClose={() => setInvoiceDetail({ open: false })}
          projectId={projectId}
          documentNature={invoiceDetail.documentNature}
        />
      ) : null}

      <U3121BillingSummaryTimesheetDetailDlg
        isOpen={timesheetDetailOpen}
        onClose={() => setTimesheetDetailOpen(false)}
        projectId={projectId}
      />
    </div>
  );
};

export default U3121ProjectBillingSummaryBody;
